import { useState } from 'react';
import { BookOpen, MessageCircle, Library, Sparkles, Target, Award, FileText } from 'lucide-react';
import TabButton from './ui/TabButton';
import ModuleCard from './ui/ModuleCard';
import ChatInterface from './ChatInterface';
import ModuleReader from './ModuleReader';
import PromptLibrary from './PromptLibrary';
import Research from './Research';
import type { LearningModule } from '../services/contentService';

export default function Dashboard() {
  const [activeTab, setActiveTab] = useState<'lessons' | 'chat' | 'library' | 'research'>('lessons');
  const [activeModule, setActiveModule] = useState<LearningModule | null>(null);
  const [completedModules, setCompletedModules] = useState<Set<string>>(new Set());

  // Define all modules
  const modules: LearningModule[] = [
    {
      id: 'module-01',
      number: 1,
      title: 'Introduction to Generative AI',
      description: 'Understand the fundamentals of AI and how it applies to occupational therapy practice',
      content: '',
      duration: '30 min',
      level: 'Beginner',
      filepath: '/content/lessons/01-introduction-to-generative-ai.md'
    },
    {
      id: 'module-02',
      number: 2,
      title: 'Getting Started with ChatGPT & Claude',
      description: 'Learn the basics of conversational AI tools and start your first conversations',
      content: '',
      duration: '35 min',
      level: 'Beginner',
      filepath: '/content/lessons/02-getting-started-with-chatgpt-claude.md'
    },
    {
      id: 'module-03',
      number: 3,
      title: 'Prompt Engineering for OTs',
      description: 'Master the art of writing effective AI prompts using the CRAFT framework',
      content: '',
      duration: '40 min',
      level: 'Intermediate',
      filepath: '/content/lessons/03-prompt-engineering-for-ots.md'
    },
    {
      id: 'module-04',
      number: 4,
      title: 'AI for Clinical Documentation',
      description: 'Streamline your documentation workflow with AI assistance',
      content: '',
      duration: '45 min',
      level: 'Intermediate',
      filepath: '/content/lessons/04-clinical-documentation-with-ai.md'
    },
    {
      id: 'module-05',
      number: 5,
      title: 'Treatment Planning with AI',
      description: 'Generate evidence-based treatment ideas and solve complex cases',
      content: '',
      duration: '40 min',
      level: 'Intermediate',
      filepath: '/content/lessons/05-treatment-planning-with-ai.md'
    },
    {
      id: 'module-06',
      number: 6,
      title: 'Creating Patient Education Materials',
      description: 'Build custom resources in minutes with AI assistance',
      content: '',
      duration: '35 min',
      level: 'Intermediate',
      filepath: '/content/lessons/06-creating-patient-education-materials.md'
    },
    {
      id: 'module-07',
      number: 7,
      title: 'Research & Evidence Synthesis',
      description: 'Find and analyze research faster with AI-powered tools',
      content: '',
      duration: '35 min',
      level: 'Advanced',
      filepath: '/content/lessons/07-research-evidence-synthesis.md'
    },
    {
      id: 'module-08',
      number: 8,
      title: 'Ethics, HIPAA & Best Practices',
      description: 'Use AI responsibly in healthcare with proper compliance',
      content: '',
      duration: '40 min',
      level: 'Advanced',
      filepath: '/content/lessons/08-ethics-hipaa-best-practices.md'
    }
  ];

  const handleStartModule = (moduleId: string) => {
    const module = modules.find(m => m.id === moduleId);
    if (module) {
      setActiveModule(module);
    }
  };

  const handleToggleComplete = (isCompleted: boolean) => {
    if (activeModule) {
      setCompletedModules(prev => {
        const newSet = new Set(prev);
        if (isCompleted) {
          newSet.add(activeModule.id);
        } else {
          newSet.delete(activeModule.id);
        }
        return newSet;
      });
    }
  };

  const handleNextModule = () => {
    if (!activeModule) return;
    const currentIndex = modules.findIndex(m => m.id === activeModule.id);
    if (currentIndex < modules.length - 1) {
      setActiveModule(modules[currentIndex + 1]);
    }
  };

  const handlePreviousModule = () => {
    if (!activeModule) return;
    const currentIndex = modules.findIndex(m => m.id === activeModule.id);
    if (currentIndex > 0) {
      setActiveModule(modules[currentIndex - 1]);
    }
  };

  return (
    <div className="min-h-screen bg-neutral-50">
      {/* Hero Header */}
      <header className="relative bg-white border-b border-neutral-200">
        {/* Background image */}
        <div className="absolute inset-0 bg-cover bg-center bg-no-repeat pointer-events-none"
             style={{ backgroundImage: 'url(/images/banner/lg-bg.png)' }} />
        {/* Overlay for better text readability */}
        <div className="absolute inset-0 bg-gradient-to-r from-white/40 via-white/30 to-white/20 pointer-events-none" />

        <div className="relative max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          {/* Top bar */}
          <div className="flex justify-between items-center py-6 border-b border-neutral-100">
            <button
              onClick={() => {
                setActiveTab('lessons');
                setActiveModule(null);
              }}
              className="flex items-center gap-3 hover:opacity-80 transition-opacity cursor-pointer"
            >
              <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-cyan-500 rounded-xl flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-white" strokeWidth={2.5} />
              </div>
              <div>
                <h1 className="text-lg font-semibold text-neutral-900">
                  Maria's AI Hub
                </h1>
              </div>
            </button>
          </div>

          {/* Hero content */}
          <div className="py-12 sm:py-16">
            <div className="max-w-3xl">
              <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-neutral-900 leading-tight mb-8">
                Your AI Hub
                <span className="block text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-cyan-500 pb-2">
                  for Occupational Therapy
                </span>
              </h2>
              <p className="text-lg sm:text-xl text-neutral-600 leading-relaxed mb-8">
                Learn how to leverage AI tools to enhance patient care, streamline documentation,
                and unlock new possibilities in your OT practice.
              </p>

              {/* Quick stats */}
              <div className="flex flex-wrap gap-4">
                <div className="flex items-center gap-2">
                  <BookOpen className="w-4 h-4 text-neutral-400" />
                  <span className="text-sm text-neutral-500">8 Modules</span>
                </div>
                <div className="flex items-center gap-2">
                  <Target className="w-4 h-4 text-neutral-400" />
                  <span className="text-sm text-neutral-500">Self-Paced</span>
                </div>
                <div className="flex items-center gap-2">
                  <Award className="w-4 h-4 text-neutral-400" />
                  <span className="text-sm text-neutral-500">AI Tutor Included</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Navigation Tabs */}
      <div className="sticky top-0 z-10 bg-white border-b border-neutral-200 backdrop-blur-sm bg-white/95 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <nav className="flex gap-2" role="tablist">
            <TabButton
              isActive={activeTab === 'lessons'}
              onClick={() => setActiveTab('lessons')}
              icon={BookOpen}
              label="Learning Modules"
            />
            <TabButton
              isActive={activeTab === 'chat'}
              onClick={() => setActiveTab('chat')}
              icon={MessageCircle}
              label="AI Tutor"
            />
            <TabButton
              isActive={activeTab === 'library'}
              onClick={() => setActiveTab('library')}
              icon={Library}
              label="Prompt Library"
            />
            <TabButton
              isActive={activeTab === 'research'}
              onClick={() => setActiveTab('research')}
              icon={FileText}
              label="Research"
            />
          </nav>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-12">
        {activeTab === 'lessons' && (
          <div className="space-y-8">
            {/* Section header */}
            <div className="bg-gradient-to-br from-neutral-900 via-blue-950 to-neutral-900 rounded-3xl p-12 sm:p-16 border border-neutral-800">
              <h2 className="text-3xl font-bold text-white mb-3">Learning Modules</h2>
              <p className="text-neutral-300 text-lg">
                Start with the fundamentals and progress at your own pace
              </p>
            </div>

            {/* Module grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {modules.map((module) => (
                <ModuleCard
                  key={module.id}
                  number={module.number}
                  title={module.title}
                  description={module.description}
                  duration={module.duration}
                  level={module.level}
                  isCompleted={completedModules.has(module.id)}
                  onStart={() => handleStartModule(module.id)}
                />
              ))}
            </div>
          </div>
        )}

        {activeTab === 'chat' && <ChatInterface />}

        {activeTab === 'library' && <PromptLibrary />}

        {activeTab === 'research' && <Research />}
      </main>

      {/* Module Reader Overlay */}
      {activeModule && (
        <ModuleReader
          module={activeModule}
          onClose={() => setActiveModule(null)}
          onToggleComplete={handleToggleComplete}
          isCompleted={completedModules.has(activeModule.id)}
          onNext={handleNextModule}
          onPrevious={handlePreviousModule}
          hasNext={modules.findIndex(m => m.id === activeModule.id) < modules.length - 1}
          hasPrevious={modules.findIndex(m => m.id === activeModule.id) > 0}
        />
      )}
    </div>
  );
}
